import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  getFeatureAccess,
  type FeatureAccess,
} from '../services/feature-authorization-service'
import { FeatureAccessContext } from './feature-access-state'

export function FeatureAccessProvider({ children }: { children: ReactNode }) {
  const [access, setAccess] = useState<FeatureAccess | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      setAccess(await getFeatureAccess())
    } catch {
      setAccess(null)
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const can = useCallback(
    (permission: string, applicationId?: string) => {
      if (!access) return false
      if (applicationId) {
        return Boolean(
          access.applicationPermissions[applicationId]?.includes(permission),
        )
      }
      return Object.values(access.applicationPermissions).some((permissions) =>
        permissions.includes(permission),
      )
    },
    [access],
  )

  const value = useMemo(
    () => ({ access, loading, error, can, refresh }),
    [access, can, error, loading, refresh],
  )

  return (
    <FeatureAccessContext.Provider value={value}>
      {children}
    </FeatureAccessContext.Provider>
  )
}
